import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const GettingStarted = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const steps = [
    {
      title: "Complete Your Company Profile",
      description: "Get started by completing your profile for the iFLO Pro App®",
      icon: "👤",
      color: "bg-blue-50",
      link: "https://pro.iflo.com/login",
      linkText: "Go to Profile"
    },
    {
      title: "Install The iFLO Pro App®", 
      description: "Available on iOS and Android",
      icon: "⚙️",
      color: "bg-green-50",
      links: [
        {
          url: "https://apps.apple.com/us/app/iflo-pro/id6736655924",
          text: "Download for iOS"
        },
        {
          url: "https://play.google.com/store/apps/details?id=com.iflo.pro&pcampaignid=web_share",
          text: "Download for Android"
        }
      ]
    },
    {
      title: "Connect Your Systems",
      description: "Integrate iFLO Pro® with your existing management systems",
      icon: "🔄",
      color: "bg-yellow-50"
    },
    {
      title: "Explore The Hub",
      description: "Keep up with the iFLO Pro® Community by checking out our News and Education Center",
      icon: "🚀",
      color: "bg-purple-50"
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Getting Started with iFLO Pro
        </h1>
        <p className="text-xl text-gray-600">
          Let's help you get up and running with iFLO Pro in just a few steps
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.2 }}
            className={`${step.color} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300`}
          >
            <div className="flex items-start space-x-4">
              <span className="text-4xl">{step.icon}</span>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 mb-3">{step.description}</p>
                {step.link && (
                  <Link to={step.link} target="_blank" rel="noopener noreferrer">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-2 flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {step.linkText}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.button>
                  </Link>
                )}
                {step.links && (
                  <div className="flex flex-col gap-2 mt-2">
                    {step.links.map((link, index) => (
                      <Link to={link.url} target="_blank" rel="noopener noreferrer" key={index}>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {link.text}
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </motion.button>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-8 shadow-lg"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Need Assistance with iFLO Pro®?
        </h2>
        <p className="text-gray-600 mb-6">
        Check out our FAQs and Installation guide. Can't find what you're looking for? Contact us directly!
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/dashboard/install">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-yellow-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-yellow-600 transition-colors duration-300"
            >
              Installation Guide
            </motion.button>
          </Link>
          <Link to="/dashboard/faqs">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-gray-800 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-300 border border-gray-200"
            >
              View FAQs
            </motion.button>
          </Link>

          <Link to="/dashboard/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-gray-800 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-300 border border-gray-200"
            >
              Contact Us
            </motion.button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default GettingStarted;
